import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import EventsService from "../services/EventsService";
import "./TimepointCalendar.css";

const formatDuration = (ms) => {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

export const TimepointCalendar = ({ timeArray, actions, onTimepointChanged }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editTime, setEditTime] = useState("");
  const [editNote, setEditNote] = useState("");
  const [loading, setLoading] = useState(false);

  const openModal = (tp) => {
    setSelected(tp);
    setEditing(false);
    const d = new Date(tp.time);
    setEditTime(formatDatetimeLocal(d));
    setEditNote(tp.note || "");
  };

  const formatDatetimeLocal = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const handleDelete = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await EventsService.deleteTimepoint(selected.id);
      setSelected(null);
      if (onTimepointChanged) onTimepointChanged();
    } catch (e) {
      alert(t("timepoints.failedToDelete", { message: e.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const newTime = Math.floor(new Date(editTime).getTime() / 1000);
      await EventsService.editTimepoint(selected.id, {
        time: newTime,
        note: editNote || undefined,
      });
      setSelected(null);
      setEditing(false);
      if (onTimepointChanged) onTimepointChanged();
    } catch (e) {
      alert(t("timepoints.failedToSave", { message: e.message }));
    } finally {
      setLoading(false);
    }
  };

  const sorted = [...timeArray].sort((a, b) => new Date(b.time) - new Date(a.time));

  const getDateKey = (time) => {
    const d = new Date(time);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };

  const formatDateLabel = (time) => {
    const d = new Date(time);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (getDateKey(time) === getDateKey(today)) return t("common.today");
    if (getDateKey(time) === getDateKey(yesterday)) return t("common.yesterday");
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  };

  const items = [];
  let lastDateKey = null;
  for (const tp of sorted) {
    const dk = getDateKey(tp.time);
    if (dk !== lastDateKey) {
      items.push({ type: "date", dateLabel: formatDateLabel(tp.time), key: `date-${dk}` });
      lastDateKey = dk;
    }
    items.push({ type: "tp", data: tp, key: tp.id || `tp-${items.length}` });
  }

  return (
    <div className="timepoint-calendar">
      {items.length > 0 ? (
        <ul>
          {items.map((item) => {
            if (item.type === "date") {
              return (
                <li key={item.key} className="tp-date-separator">
                  <span>{item.dateLabel}</span>
                </li>
              );
            }
            const timepoint = item.data;
            const action = actions.find((a) => a.id === timepoint.actionId);
            const isTimed = !!timepoint.endTime;
            return (
              <li
                key={item.key}
                style={{ backgroundColor: action?.color || "", cursor: "pointer" }}
                onClick={() => openModal(timepoint)}
              >
                <span>{action ? action.name : t("common.unknown")}</span>
                <span>
                  {isTimed ? (
                    <>
                      {new Date(timepoint.time).toLocaleTimeString()} — {new Date(timepoint.endTime).toLocaleTimeString()}
                      <span style={{ marginLeft: 6, opacity: 0.75, fontSize: "0.85em" }}>
                        ({formatDuration(new Date(timepoint.endTime) - new Date(timepoint.time))})
                      </span>
                    </>
                  ) : (
                    new Date(timepoint.time).toLocaleTimeString()
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p style={{ color: "rgba(255,255,255,0.4)" }}>{t("timepoints.noTimepointsRecorded")}</p>
      )}

      {selected && (
        <div className="tp-modal-overlay" onClick={() => { setSelected(null); setEditing(false); }}>
          <div className="tp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tp-modal-header">
              <h3>
                {(() => {
                  const action = actions.find((a) => a.id === selected.actionId);
                  return action ? action.name : t("common.unknown");
                })()}
              </h3>
              <button className="tp-modal-close" onClick={() => { setSelected(null); setEditing(false); }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            {!editing ? (
              <div className="tp-modal-body">
                {selected.endTime ? (
                  <>
                    <div className="tp-modal-field">
                      <label>{t("timepoints.startTime")}</label>
                      <span>{new Date(selected.time).toLocaleString()}</span>
                    </div>
                    <div className="tp-modal-field">
                      <label>{t("timepoints.endTime")}</label>
                      <span>{new Date(selected.endTime).toLocaleString()}</span>
                    </div>
                    <div className="tp-modal-field">
                      <label>{t("timepoints.duration")}</label>
                      <span>{formatDuration(new Date(selected.endTime) - new Date(selected.time))}</span>
                    </div>
                  </>
                ) : (
                  <div className="tp-modal-field">
                    <label>{t("timepoints.dateTime")}</label>
                    <span>{new Date(selected.time).toLocaleString()}</span>
                  </div>
                )}
                {selected.note && (
                  <div className="tp-modal-field">
                    <label>{t("timepoints.note")}</label>
                    <span>{selected.note}</span>
                  </div>
                )}
                <div className="tp-modal-actions">
                  <button className="tp-modal-btn tp-modal-btn-edit" onClick={() => setEditing(true)} disabled={loading}>
                    <i className="fas fa-pen"></i> {t("common.edit")}
                  </button>
                  <button className="tp-modal-btn tp-modal-btn-delete" onClick={handleDelete} disabled={loading}>
                    <i className="fas fa-trash"></i> {t("common.delete")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="tp-modal-body">
                <div className="tp-modal-field">
                  <label>{t("timepoints.dateTime")}</label>
                  <input
                    type="datetime-local"
                    step="1"
                    value={editTime}
                    onChange={(e) => setEditTime(e.target.value)}
                    className="tp-modal-input"
                  />
                </div>
                <div className="tp-modal-field">
                  <label>{t("timepoints.note")}</label>
                  <input
                    type="text"
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                    placeholder={t("timepoints.notePlaceholder")}
                    className="tp-modal-input"
                  />
                </div>
                <div className="tp-modal-actions">
                  <button className="tp-modal-btn tp-modal-btn-save" onClick={handleSave} disabled={loading}>
                    <i className="fas fa-check"></i> {t("common.save")}
                  </button>
                  <button className="tp-modal-btn tp-modal-btn-cancel" onClick={() => setEditing(false)} disabled={loading}>
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
